import React from 'react';
import { Zap, Settings, ChevronRight } from 'lucide-react';
import type { OccasionFlow } from '../types';

interface OccasionChoiceProps {
  recipientName: string;
  onFlowChoice: (flow: OccasionFlow) => void;
}

export const OccasionChoice: React.FC<OccasionChoiceProps> = ({
  recipientName,
  onFlowChoice
}) => {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">
          Finally, tell us about this gift occasion
        </h1>
        <p className="text-gray-600 text-lg">
          Help us understand the context for gifting {recipientName}
        </p>
      </div>

      {/* Flow Choice Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Quick Flow Option */}
        <div
          onClick={() => onFlowChoice('quick')}
          className="bg-white rounded-3xl shadow-xl p-8 cursor-pointer transition-all hover:shadow-2xl hover:scale-105"
        >
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-6">
              <Zap className="w-8 h-8 text-yellow-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Quick Flow</h3>
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="text-3xl font-bold text-yellow-600">30s</span>
              <span className="text-gray-500">• 2 questions</span>
            </div>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Just the essentials - occasion, budget, and the feeling you want to create. 
              Perfect when you're ready to see recommendations quickly.
            </p>
            
            <div className="space-y-3 text-sm text-gray-500 mb-6">
              <div className="flex items-center justify-center gap-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                <span>Occasion type & budget</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                <span>Desired emotional impact</span>
              </div>
            </div>

            <button className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-xl font-medium transition-colors">
              Quick Setup <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Standard Flow Option */}
        <div
          onClick={() => onFlowChoice('standard')}
          className="bg-white rounded-3xl shadow-xl p-8 cursor-pointer transition-all hover:shadow-2xl hover:scale-105"
        >
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-6">
              <Settings className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Standard Flow</h3>
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="text-3xl font-bold text-purple-600">~2min</span>
              <span className="text-gray-500">• 4 questions</span>
            </div>
            <p className="text-gray-600 mb-6 leading-relaxed">
              More detailed context and emotional goals for highly nuanced recommendations. 
              Includes safety vs surprise preferences.
            </p>
            
            <div className="space-y-3 text-sm text-gray-500 mb-6">
              <div className="flex items-center justify-center gap-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span>Multiple emotional goals</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span>Special occasion context</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span>Risk vs safety preferences</span>
              </div>
            </div>

            <button className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-colors">
              Detailed Setup <Settings className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="text-center mt-8">
        <p className="text-gray-500 text-sm">
          🎯 The more context you provide, the more personalized your recommendations will be
        </p>
      </div>
    </div>
  );
}; 